import { ApiCommunications, NullPromise } from '../../webmodule-common/interop/webmodule-interop';
import type {
  FabricationJobSummaryView,
  FabricationJobSummaryViewResultPaginated,
  InputAddQuoteItemToFabricationJob,
  InputCleanupQuoteFabricationData,
  InputCreateFabricationJob,
  InputCreateQuoteItemFabrications,
  InputDeleteFabricationJob,
  InputRecordQuoteItemDelivery,
  InputRemoveQuoteItemFromFabricationJob,
  InputSetQuoteItemFactoryTrackingReference,
  InputTransitionFabricationJobState,
  InputUpdateFabricationJob,
  InputUpdateFabricationJobItemUnitOverrides,
  RequestGetFabricationJob,
  RequestGetFabricationJobSummary,
  RequestGetFabricationJobsByIds,
  RequestGetFabricationJobTotalsByState,
  RequestGetQuoteFabricationSummaries,
  RequestGetQuoteItemFabricationStatuses,
  ResultCleanupQuoteFabricationData,
  ResultCreateFabricationJob,
  ResultCreateQuoteItemFabrications,
  ResultGetFabricationJob,
  ResultGetFabricationJobTotalsByState,
  ResultGetQuoteFabricationSummaries,
  ResultGetQuoteItemFabricationStatuses,
  ResultRecordQuoteItemDelivery,
  ResultSetQuoteItemFactoryTrackingReference,
  ResultUpdateFabricationJob
} from './dealer-api-interface-all';
import type {
  FabricationJobApi,
  InputInvalidateOptimization,
  InputLockInOptimization,
  InputStartOptimization,
  RequestGetOptimizationResult,
  ResultGetOptimizationResult,
  ResultOptimizationMutation
} from './fabrication-job-api';

export class FabricationJobApiImplementation implements FabricationJobApi {
  api: ApiCommunications;

  constructor(api: ApiCommunications) {
    this.api = api;
  }

  async getFabricationJob(request: RequestGetFabricationJob): NullPromise<ResultGetFabricationJob> {
    return await this.api.post<ResultGetFabricationJob>('api/FabricationJob/GetFabricationJob', request);
  }

  async getFabricationJobSummary(
    request: RequestGetFabricationJobSummary
  ): NullPromise<FabricationJobSummaryViewResultPaginated> {
    return await this.api.post<FabricationJobSummaryViewResultPaginated>(
      'api/FabricationJob/GetFabricationJobSummary',
      request
    );
  }

  async getFabricationJobsByIds(request: RequestGetFabricationJobsByIds): NullPromise<FabricationJobSummaryView[]> {
    return await this.api.post<FabricationJobSummaryView[]>('api/FabricationJob/GetFabricationJobsByIds', request);
  }

  async getFabricationJobTotalsByState(
    request: RequestGetFabricationJobTotalsByState
  ): NullPromise<ResultGetFabricationJobTotalsByState> {
    return await this.api.post<ResultGetFabricationJobTotalsByState>(
      'api/FabricationJob/GetFabricationJobTotalsByState',
      request
    );
  }

  async getQuoteFabricationSummaries(
    request: RequestGetQuoteFabricationSummaries
  ): NullPromise<ResultGetQuoteFabricationSummaries> {
    return await this.api.post<ResultGetQuoteFabricationSummaries>(
      'api/FabricationJob/GetQuoteFabricationSummaries',
      request
    );
  }

  async getQuoteItemFabricationStatuses(
    request: RequestGetQuoteItemFabricationStatuses
  ): NullPromise<ResultGetQuoteItemFabricationStatuses> {
    return await this.api.post<ResultGetQuoteItemFabricationStatuses>(
      'api/FabricationJob/GetQuoteItemFabricationStatuses',
      request
    );
  }

  async createFabricationJob(input: InputCreateFabricationJob): NullPromise<ResultCreateFabricationJob> {
    return await this.api.post<ResultCreateFabricationJob>('api/FabricationJob/CreateFabricationJob', input);
  }

  async updateFabricationJob(input: InputUpdateFabricationJob): NullPromise<ResultUpdateFabricationJob> {
    return await this.api.post<ResultUpdateFabricationJob>('api/FabricationJob/UpdateFabricationJob', input);
  }

  async deleteFabricationJob(input: InputDeleteFabricationJob): NullPromise<ResultUpdateFabricationJob> {
    return await this.api.post<ResultUpdateFabricationJob>('api/FabricationJob/DeleteFabricationJob', input);
  }

  async addQuoteItemToFabricationJob(
    input: InputAddQuoteItemToFabricationJob
  ): NullPromise<ResultUpdateFabricationJob> {
    return await this.api.post<ResultUpdateFabricationJob>('api/FabricationJob/AddQuoteItemToFabricationJob', input);
  }

  async removeQuoteItemFromFabricationJob(
    input: InputRemoveQuoteItemFromFabricationJob
  ): NullPromise<ResultUpdateFabricationJob> {
    return await this.api.post<ResultUpdateFabricationJob>(
      'api/FabricationJob/RemoveQuoteItemFromFabricationJob',
      input
    );
  }

  async updateFabricationJobItemUnitOverrides(
    input: InputUpdateFabricationJobItemUnitOverrides
  ): NullPromise<ResultUpdateFabricationJob> {
    return await this.api.post<ResultUpdateFabricationJob>(
      'api/FabricationJob/UpdateFabricationJobItemUnitOverrides',
      input
    );
  }

  async transitionFabricationJobState(
    input: InputTransitionFabricationJobState
  ): NullPromise<ResultUpdateFabricationJob> {
    return await this.api.post<ResultUpdateFabricationJob>('api/FabricationJob/TransitionFabricationJobState', input);
  }

  async recordQuoteItemDelivery(input: InputRecordQuoteItemDelivery): NullPromise<ResultRecordQuoteItemDelivery> {
    return await this.api.post<ResultRecordQuoteItemDelivery>('api/FabricationJob/RecordQuoteItemDelivery', input);
  }

  async setQuoteItemFactoryTrackingReference(
    input: InputSetQuoteItemFactoryTrackingReference
  ): NullPromise<ResultSetQuoteItemFactoryTrackingReference> {
    return await this.api.post<ResultSetQuoteItemFactoryTrackingReference>(
      'api/FabricationJob/SetQuoteItemFactoryTrackingReference',
      input
    );
  }

  async createQuoteItemFabrications(
    input: InputCreateQuoteItemFabrications
  ): NullPromise<ResultCreateQuoteItemFabrications> {
    return await this.api.post<ResultCreateQuoteItemFabrications>(
      'api/FabricationJob/CreateQuoteItemFabrications',
      input
    );
  }

  async cleanupQuoteFabricationData(
    input: InputCleanupQuoteFabricationData
  ): NullPromise<ResultCleanupQuoteFabricationData> {
    return await this.api.post<ResultCleanupQuoteFabricationData>(
      'api/FabricationJob/CleanupQuoteFabricationData',
      input
    );
  }

  async getOptimizationResult(request: RequestGetOptimizationResult): NullPromise<ResultGetOptimizationResult> {
    return await this.api.post<ResultGetOptimizationResult>('api/FabricationJob/GetOptimizationResult', request);
  }

  async startOptimization(input: InputStartOptimization): NullPromise<ResultOptimizationMutation> {
    return await this.api.post<ResultOptimizationMutation>('api/FabricationJob/StartOptimization', input);
  }

  async lockInOptimization(input: InputLockInOptimization): NullPromise<ResultOptimizationMutation> {
    return await this.api.post<ResultOptimizationMutation>('api/FabricationJob/LockInOptimization', input);
  }

  async cancelOptimization(input: InputInvalidateOptimization): NullPromise<ResultOptimizationMutation> {
    return await this.api.post<ResultOptimizationMutation>('api/FabricationJob/InvalidateOptimization', input);
  }
}
